"use client";

import { useId } from "react";
import { isMovementActivityPostPayload } from "@/lib/movement/activity-post-payload";
import type { CyclingTemplateData } from "../cycling-template";
import { MovementActivityDisplayPreview } from "./movement-activity-display-preview";
import { ActivityScenePreview } from "./reference-preview-primitives";

type CyclingDisplayPreviewProps = {
  data: CyclingTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * A mountain pass at golden hour: the sun sits on the saddle between the
 * ridges, and the road winds out of the foreground towards it. A road bike is
 * parked on the left verge; on the right a col marker stone ("COL · 2 KM")
 * wears a helmet.
 *
 * One panoramic SVG (640×220, `slice`): a 301px feed card shows the composed
 * centre, a full-width post reveals more ridgeline. The road's near end sits
 * under the bottom-centre participant pill. Motion is one compositor-only CSS
 * loop (the sun's flare breathing), off under reduced motion.
 */
const CYCLING_KEYFRAMES = `
@keyframes cyc-sun { 0%, 100% { opacity: 0.85 } 50% { opacity: 1 } }
.cyc-sun { animation: cyc-sun 5s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .cyc-sun { animation: none } }
`;

type Point = { x: number; y: number };

const bezier = (p: Point[], t: number): Point => {
  const u = 1 - t;
  return {
    x:
      u * u * u * p[0].x +
      3 * u * u * t * p[1].x +
      3 * u * t * t * p[2].x +
      t * t * t * p[3].x,
    y:
      u * u * u * p[0].y +
      3 * u * u * t * p[1].y +
      3 * u * t * t * p[2].y +
      t * t * t * p[3].y,
  };
};

/**
 * The road as a perspective ribbon: points along a cubic centreline, offset
 * by a half-width that shrinks with distance. Computed once per module.
 */
const ROAD = (() => {
  const centre: Point[] = [
    { x: 320, y: 232 },
    { x: 190, y: 180 },
    { x: 452, y: 150 },
    { x: 330, y: 121 },
  ];
  const steps = 32;
  const left: Point[] = [];
  const right: Point[] = [];
  const mid: Point[] = [];
  const widths: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p = bezier(centre, t);
    const a = bezier(centre, Math.max(0, t - 0.01));
    const b = bezier(centre, Math.min(1, t + 0.01));
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    // Horizontal-ish normal: perspective road widths read along x.
    const nx = -dy / len;
    const ny = dx / len;
    const w = 2 + 128 * (1 - t) ** 1.8;
    left.push({ x: p.x + nx * w, y: p.y + ny * w * 0.35 });
    right.push({ x: p.x - nx * w, y: p.y - ny * w * 0.35 });
    mid.push(p);
    widths.push(w);
  }
  const toPath = (pts: Point[]) =>
    pts
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
      )
      .join(" ");
  const surface = `${toPath(left)} ${right
    .slice()
    .reverse()
    .map((p) => `L${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ")} Z`;
  const inset = (edge: Point[], k: number) =>
    edge.map((p, i) => ({
      x: p.x + (mid[i].x - p.x) * k,
      y: p.y + (mid[i].y - p.y) * k,
    }));
  const dashes: string[] = [];
  for (let i = 0; i < steps - 2; i += 2) {
    const a = mid[i];
    const b = mid[i + 1];
    dashes.push(
      `M${a.x.toFixed(1)} ${a.y.toFixed(1)} L${b.x.toFixed(1)} ${b.y.toFixed(1)}`,
    );
  }
  return {
    surface,
    leftLine: toPath(inset(left, 0.08)),
    rightLine: toPath(inset(right, 0.08)),
    dashes,
    dashWidths: dashes.map((_, i) => Math.max(0.6, widths[i * 2] * 0.035)),
    edgeWidths: widths,
  };
})();

export function CyclingDisplayPreview({
  data,
  participants,
}: CyclingDisplayPreviewProps) {
  const uid = useId().replace(/[^\w-]/g, "");
  const id = (name: string) => `cyc-${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;

  if (isMovementActivityPostPayload(data)) {
    return (
      <MovementActivityDisplayPreview
        activityId="cycling"
        label="Cycling"
        payload={data}
      />
    );
  }

  return (
    <ActivityScenePreview
      label="Cycling"
      backgroundClassName="bg-[#1b1d3f]"
      participants={participants}
      scene={
        <>
          <style href="cycling-scene-keyframes" precedence="default">
            {CYCLING_KEYFRAMES}
          </style>
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 640 220"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id={id("sky")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#1c2353" />
                <stop offset="0.25" stopColor="#4b3a7c" />
                <stop offset="0.45" stopColor="#c45a62" />
                <stop offset="0.56" stopColor="#ff9a5a" />
                <stop offset="0.62" stopColor="#ffd38a" />
              </linearGradient>
              <radialGradient id={id("flare")}>
                <stop offset="0" stopColor="#fffbe6" stopOpacity="1" />
                <stop offset="0.12" stopColor="#ffe9a8" stopOpacity="0.9" />
                <stop offset="0.35" stopColor="#ffb468" stopOpacity="0.35" />
                <stop offset="1" stopColor="#ff8a4a" stopOpacity="0" />
              </radialGradient>
              <linearGradient id={id("far")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#d07a86" />
                <stop offset="1" stopColor="#e6967a" />
              </linearGradient>
              <linearGradient id={id("mid")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#7c3f68" />
                <stop offset="1" stopColor="#9a5568" />
              </linearGradient>
              <linearGradient id={id("near")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#3b2448" />
                <stop offset="1" stopColor="#24183a" />
              </linearGradient>
              <linearGradient id={id("ground")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#5a5a2a" />
                <stop offset="0.35" stopColor="#2e3a1e" />
                <stop offset="1" stopColor="#141b10" />
              </linearGradient>
              <linearGradient id={id("road")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#d9a07a" />
                <stop offset="0.12" stopColor="#6b4a50" />
                <stop offset="0.45" stopColor="#2c2430" />
                <stop offset="1" stopColor="#17141c" />
              </linearGradient>
              <linearGradient id={id("frame")} x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#ff7348" />
                <stop offset="0.5" stopColor="#f0303e" />
                <stop offset="1" stopColor="#9c1230" />
              </linearGradient>
              <linearGradient id={id("stone")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#fff6e6" />
                <stop offset="0.6" stopColor="#e9dccb" />
                <stop offset="1" stopColor="#a8927e" />
              </linearGradient>
              <linearGradient id={id("helmet")} x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#7af0e0" />
                <stop offset="0.5" stopColor="#18b6b0" />
                <stop offset="1" stopColor="#0b5f68" />
              </linearGradient>
            </defs>

            <rect width="640" height="140" fill={url("sky")} />

            {/* Thin sunset cloud streaks */}
            {[
              { x: 90, y: 40, w: 120 },
              { x: 420, y: 30, w: 150 },
              { x: 230, y: 64, w: 90 },
              { x: 470, y: 70, w: 80 },
            ].map((cloud) => (
              <rect
                key={cloud.x}
                x={cloud.x}
                y={cloud.y}
                width={cloud.w}
                height="2.6"
                rx="1.3"
                fill="#ffc7a8"
                opacity="0.38"
              />
            ))}

            {/* The sun on the saddle */}
            <circle
              className="cyc-sun"
              cx="326"
              cy="108"
              r="120"
              fill={url("flare")}
            />
            <circle cx="326" cy="108" r="15" fill="#fff6d8" />

            {/* Ridges, far → near: haze lifts the far ones */}
            <path
              d="M-10 116 L40 92 L80 104 L130 74 L180 98 L232 84 L280 108 L326 116 L372 104 L420 80 L470 96 L520 70 L570 94 L650 82 L650 130 L-10 130 Z"
              fill={url("far")}
            />
            {/* Sunlit snow on the far peaks */}
            <path
              d="M130 74 L118 80 L126 81 L134 78 L142 81 Z"
              fill="#fff0dc"
              opacity="0.8"
            />
            <path
              d="M520 70 L508 77 L516 78 L524 75 L532 78 Z"
              fill="#fff0dc"
              opacity="0.8"
            />
            <path
              d="M-10 124 L60 102 L120 116 L190 96 L250 116 L300 124 L360 122 L410 104 L470 118 L540 98 L600 114 L650 106 L650 140 L-10 140 Z"
              fill={url("mid")}
            />
            <path
              d="M-10 132 L50 116 L110 128 L170 112 L238 126 L300 128 L360 128 L420 120 L480 132 L560 112 L650 126 L650 150 L-10 150 Z"
              fill={url("near")}
            />
            {/* Warm haze where the sun meets the land */}
            <ellipse
              cx="326"
              cy="122"
              rx="160"
              ry="16"
              fill="#ffb27a"
              opacity="0.28"
            />

            {/* Meadow the road runs through */}
            <path
              d="M-10 136 Q160 120 330 124 T650 134 L650 220 L-10 220 Z"
              fill={url("ground")}
            />
            {/* Rim light along the meadow crest */}
            <path
              d="M-10 136 Q160 120 330 124 T650 134"
              fill="none"
              stroke="#ffcf8a"
              strokeOpacity="0.45"
              strokeWidth="1.2"
            />

            {/* The road */}
            <path d={ROAD.surface} fill={url("road")} />
            <path
              d={ROAD.leftLine}
              fill="none"
              stroke="#fff3e2"
              strokeOpacity="0.8"
              strokeWidth="1.6"
            />
            <path
              d={ROAD.rightLine}
              fill="none"
              stroke="#fff3e2"
              strokeOpacity="0.8"
              strokeWidth="1.6"
            />
            {ROAD.dashes.map((d, i) => (
              <path
                key={d}
                d={d}
                fill="none"
                stroke="#ffc83a"
                strokeWidth={ROAD.dashWidths[i]}
                strokeLinecap="round"
              />
            ))}

            {/* Pines framing the outer edges (seen on wider cards) */}
            {[
              { x: 40, h: 46 },
              { x: 66, h: 34 },
              { x: 584, h: 44 },
              { x: 610, h: 56 },
            ].map((tree) => (
              <g key={tree.x}>
                <polygon
                  points={`${tree.x},${170 - tree.h} ${tree.x - tree.h * 0.28},170 ${tree.x + tree.h * 0.28},170`}
                  fill="#14200f"
                />
                <rect
                  x={tree.x - 1.5}
                  y="168"
                  width="3"
                  height="8"
                  fill="#1b130d"
                />
              </g>
            ))}

            {/* Road bike parked on the left verge */}
            <g transform="translate(182 102)">
              <ellipse
                cx="56"
                cy="66"
                rx="58"
                ry="4"
                fill="#000"
                opacity="0.4"
              />
              {[20, 92].map((cx) => (
                <g key={cx}>
                  <circle
                    cx={cx}
                    cy="46"
                    r="19"
                    fill="none"
                    stroke="#0e0d12"
                    strokeWidth="3.6"
                  />
                  <circle
                    cx={cx}
                    cy="46"
                    r="15.4"
                    fill="none"
                    stroke="#24222c"
                    strokeWidth="4.6"
                  />
                  {[0, 30, 60, 90, 120, 150].map((angle) => (
                    <line
                      key={angle}
                      x1={cx + 13 * Math.cos((angle * Math.PI) / 180)}
                      y1={46 + 13 * Math.sin((angle * Math.PI) / 180)}
                      x2={cx - 13 * Math.cos((angle * Math.PI) / 180)}
                      y2={46 - 13 * Math.sin((angle * Math.PI) / 180)}
                      stroke="#c9c4d6"
                      strokeOpacity="0.55"
                      strokeWidth="0.6"
                    />
                  ))}
                  <circle cx={cx} cy="46" r="2.4" fill="#d8d4e2" />
                  {/* Sun glint on the rim */}
                  <path
                    d={`M${cx + 9} ${46 - 16} A19 19 0 0 1 ${cx + 19} ${46}`}
                    fill="none"
                    stroke="#ffcf8a"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </g>
              ))}
              {/* Frame */}
              <g
                fill="none"
                stroke={url("frame")}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M50 48 L40 16" strokeWidth="4" />
                <path d="M40 16 L80 19" strokeWidth="3.6" />
                <path d="M50 48 L82 28" strokeWidth="4.6" />
                <path d="M50 48 L20 46" strokeWidth="2.8" />
                <path d="M41 20 L20 46" strokeWidth="2.6" />
              </g>
              <path
                d="M80 17 L83 30"
                stroke="#1b1a22"
                strokeWidth="4.6"
                strokeLinecap="round"
              />
              <path
                d="M83 28 L92 46"
                stroke="#1b1a22"
                strokeWidth="2.8"
                strokeLinecap="round"
              />
              {/* Rim light on the top tube + down tube */}
              <path
                d="M41 14.6 L79 17.4"
                stroke="#ffd9a8"
                strokeWidth="0.9"
                strokeOpacity="0.9"
              />
              <path
                d="M51 45 L80 26.6"
                stroke="#ffd9a8"
                strokeWidth="0.8"
                strokeOpacity="0.7"
              />
              {/* Seatpost + saddle */}
              <path d="M40 16 L38.6 9" stroke="#1b1a22" strokeWidth="2.2" />
              <path d="M30 8.4 Q38 6 48 8.4 L46 10 L32 10 Z" fill="#141319" />
              {/* Stem + drop bars */}
              <path
                d="M80 16 L88 14"
                stroke="#1b1a22"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
              <path
                d="M88 14 Q95 14 95 19 Q95 24 90 24"
                fill="none"
                stroke="#141319"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
              {/* Chainring + crank */}
              <circle
                cx="50"
                cy="48"
                r="6.4"
                fill="none"
                stroke="#bdb8c8"
                strokeWidth="1.6"
              />
              <path
                d="M50 48 L56 54"
                stroke="#2a2833"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <rect
                x="54"
                y="53.4"
                width="6"
                height="2"
                rx="1"
                fill="#1b1a22"
              />
              {/* Bottle cage */}
              <rect
                x="60"
                y="33"
                width="5"
                height="12"
                rx="2"
                transform="rotate(-57 62 39)"
                fill="#e9f2ff"
              />
            </g>

            {/* Col marker stone + helmet, right verge */}
            <g transform="translate(404 92)">
              <ellipse
                cx="22"
                cy="64"
                rx="30"
                ry="3.6"
                fill="#000"
                opacity="0.4"
              />
              <path
                d="M2 64 L2 22 Q2 6 22 6 Q42 6 42 22 L42 64 Z"
                fill={url("stone")}
              />
              <path
                d="M2 30 L2 22 Q2 6 22 6 Q42 6 42 22 L42 30 Z"
                fill="#d8262c"
              />
              <path
                d="M5 21 Q6 10 20 9"
                fill="none"
                stroke="#ff8a7a"
                strokeWidth="1.2"
              />
              <text
                x="22"
                y="41"
                textAnchor="middle"
                fontSize="7"
                fontWeight="800"
                fill="#3a2a22"
                letterSpacing="1"
                style={{ fontFamily: "inherit" }}
              >
                COL
              </text>
              <text
                x="22"
                y="54"
                textAnchor="middle"
                fontSize="11"
                fontWeight="900"
                fill="#1e1612"
                style={{ fontFamily: "inherit" }}
              >
                2 km
              </text>
              {/* Helmet resting on top */}
              <g transform="translate(6 -6)">
                <path
                  d="M2 14 Q2 0 18 0 Q32 0 34 12 L30 14 Z"
                  fill={url("helmet")}
                />
                {[9, 16, 23].map((x) => (
                  <path
                    key={x}
                    d={`M${x} 4 Q${x + 2} 8 ${x + 1} 12`}
                    stroke="#063238"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                ))}
                <path
                  d="M5 6 Q10 1 18 1"
                  fill="none"
                  stroke="#e6fffb"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
                <rect
                  x="2"
                  y="13"
                  width="29"
                  height="2.4"
                  rx="1.2"
                  fill="#123036"
                />
              </g>
            </g>

            {/* A few swifts heading for the sun */}
            {[
              { x: 270, y: 74, s: 1 },
              { x: 286, y: 66, s: 0.75 },
              { x: 372, y: 58, s: 0.85 },
            ].map((bird) => (
              <path
                key={bird.x}
                d={`M${bird.x - 5 * bird.s} ${bird.y - 2 * bird.s} Q${bird.x - 2 * bird.s} ${bird.y - 3 * bird.s} ${bird.x} ${bird.y} Q${bird.x + 2 * bird.s} ${bird.y - 3 * bird.s} ${bird.x + 5 * bird.s} ${bird.y - 2 * bird.s}`}
                fill="none"
                stroke="#2a1834"
                strokeWidth="1.1"
                strokeLinecap="round"
              />
            ))}
          </svg>
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_125%_at_50%_45%,transparent_52%,rgba(18,10,30,0.5)_100%)]" />
      }
    />
  );
}
